/** 系统配置 Mock数据 */

import type {
  SystemConfig, RBACRole, AuditLog, SensitiveOperation, PermissionMatrixRow,
  SlaNode, TempThreshold, CurrencyConfig, RouteConfig, TemplateConfig,
  AIChatMessage, EvidenceReportMetadata,
  TokenLink, PublicVerification, AICapability, HITLRule, AIStats,
} from '../types';
import type { LiabilitySegment } from '../types/order';
import {
  ConfigType, ChatMessageType, AICapabilityStatus, HITLPriority,
} from '../types';
import { randomPick, randomTimestamp, randomInt } from '../utils/mockHelper';

export const mockSystemConfigs: SystemConfig[] = [
  { id: 'CFG001', key: 'system.name', value: 'BluPlanet OS', type: ConfigType.STRING, group: '基础设置', description: '系统名称', updatedBy: '管理员', updatedAt: '2025-06-01T00:00:00Z' },
  { id: 'CFG002', key: 'system.logo', value: '/logo.png', type: ConfigType.STRING, group: '基础设置', description: '系统Logo路径', updatedBy: '管理员', updatedAt: '2025-06-01T00:00:00Z' },
  { id: 'CFG003', key: 'trade.maxOrderAmount', value: '500000', type: ConfigType.NUMBER, group: '交易配置', description: '单笔订单最大金额（USD）', updatedBy: '风控主管', updatedAt: '2025-06-15T00:00:00Z' },
  { id: 'CFG004', key: 'trade.minOrderAmount', value: '1000', type: ConfigType.NUMBER, group: '交易配置', description: '单笔订单最小金额（USD）', updatedBy: '风控主管', updatedAt: '2025-06-15T00:00:00Z' },
  { id: 'CFG005', key: 'trade.defaultCurrency', value: 'USD', type: ConfigType.STRING, group: '交易配置', description: '默认交易货币', updatedBy: '管理员', updatedAt: '2025-06-01T00:00:00Z' },
  { id: 'CFG006', key: 'iot.tempRange', value: '{"min":0,"max":4}', type: ConfigType.JSON, group: 'IoT配置', description: '冷藏温度范围（°C）', updatedBy: 'IoT管理员', updatedAt: '2025-06-20T00:00:00Z' },
  { id: 'CFG007', key: 'iot.humidityRange', value: '{"min":50,"max":70}', type: ConfigType.JSON, group: 'IoT配置', description: '湿度范围（%）', updatedBy: 'IoT管理员', updatedAt: '2025-06-20T00:00:00Z' },
  { id: 'CFG008', key: 'iot.dataInterval', value: '15', type: ConfigType.NUMBER, group: 'IoT配置', description: '数据上报间隔（分钟）', updatedBy: 'IoT管理员', updatedAt: '2025-06-20T00:00:00Z' },
  { id: 'CFG009', key: 'shipment.defaultCarrier', value: 'COSCO SHIPPING', type: ConfigType.STRING, group: '物流配置', description: '默认承运商', updatedBy: '物流主管', updatedAt: '2025-06-10T00:00:00Z' },
  { id: 'CFG010', key: 'shipment.insuranceRate', value: '0.003', type: ConfigType.NUMBER, group: '物流配置', description: '保费费率', updatedBy: '物流主管', updatedAt: '2025-06-10T00:00:00Z' },
  { id: 'CFG011', key: 'finance.bluCreditLimit', value: '100000', type: ConfigType.NUMBER, group: '财务配置', description: 'BluCredit最大额度（USD）', updatedBy: '财务主管', updatedAt: '2025-06-18T00:00:00Z' },
  { id: 'CFG012', key: 'finance.paymentDueDays', value: '30', type: ConfigType.NUMBER, group: '财务配置', description: '付款期限（天）', updatedBy: '财务主管', updatedAt: '2025-06-18T00:00:00Z' },
  { id: 'CFG013', key: 'notification.emailEnabled', value: 'true', type: ConfigType.BOOLEAN, group: '通知配置', description: '是否启用邮件通知', updatedBy: '管理员', updatedAt: '2025-06-01T00:00:00Z' },
  { id: 'CFG014', key: 'notification.smsEnabled', value: 'false', type: ConfigType.BOOLEAN, group: '通知配置', description: '是否启用短信通知', updatedBy: '管理员', updatedAt: '2025-06-01T00:00:00Z' },
  { id: 'CFG015', key: 'security.sessionTimeout', value: '3600', type: ConfigType.NUMBER, group: '安全配置', description: '会话超时时间（秒）', updatedBy: '安全主管', updatedAt: '2025-06-25T00:00:00Z' },
];

// ===== G05 RBAC 角色（8 个业务角色） =====

export const mockRBACRoles: RBACRole[] = [
  {
    id: 'R001', name: '超级管理员', description: '拥有系统全部权限，可执行所有操作',
    permissions: ['*:*'],
    permissionCount: 16, userCount: 3, status: 'active',
    createdAt: '2024-06-01T00:00:00Z', updatedAt: '2025-01-15T08:00:00Z',
  },
  {
    id: 'R002', name: '运营经理', description: '管理日常运营流程、订单履约监控',
    permissions: ['order:view', 'order:edit', 'order:create', 'container:view', 'container:edit', 'dashboard:view', 'report:view', 'report:export', 'task:view', 'task:assign', 'finance:view', 'buyer:view'],
    permissionCount: 12, userCount: 8, status: 'active',
    createdAt: '2024-06-01T00:00:00Z', updatedAt: '2025-01-14T16:30:00Z',
  },
  {
    id: 'R003', name: '产地组长', description: '管理产地验货、预冷处理、工厂装箱',
    permissions: ['order:view', 'container:view', 'task:view', 'task:assign', 'task:execute', 'report:view'],
    permissionCount: 6, userCount: 5, status: 'active',
    createdAt: '2024-06-15T00:00:00Z', updatedAt: '2025-01-10T10:00:00Z',
  },
  {
    id: 'R004', name: '船务专员', description: '负责船务订舱、港口集货、海运跟踪',
    permissions: ['order:view', 'container:view', 'container:edit', 'shipping:view', 'shipping:manage'],
    permissionCount: 5, userCount: 4, status: 'active',
    createdAt: '2024-07-01T00:00:00Z', updatedAt: '2025-01-12T09:00:00Z',
  },
  {
    id: 'R005', name: '质检员', description: '执行装车前检验、到仓验收、质量报告',
    permissions: ['order:view', 'container:view', 'task:execute', 'report:view'],
    permissionCount: 4, userCount: 12, status: 'active',
    createdAt: '2024-07-01T00:00:00Z', updatedAt: '2025-01-08T14:00:00Z',
  },
  {
    id: 'R006', name: '财务专员', description: '管理财务收款、汇率、BluCredit 额度',
    permissions: ['finance:view', 'finance:edit', 'finance:approve', 'order:view', 'buyer:view', 'report:view', 'report:export', 'system:config_view'],
    permissionCount: 8, userCount: 5, status: 'active',
    createdAt: '2024-06-01T00:00:00Z', updatedAt: '2025-01-15T07:00:00Z',
  },
  {
    id: 'R007', name: '迪拜团队', description: '目的港清关、陆运配送、签收管理',
    permissions: ['order:view', 'container:view', 'customs:view', 'customs:manage', 'task:view', 'task:execute', 'report:view'],
    permissionCount: 7, userCount: 6, status: 'active',
    createdAt: '2024-08-01T00:00:00Z', updatedAt: '2025-01-13T11:00:00Z',
  },
  {
    id: 'R008', name: 'IoT管理员', description: '管理IoT设备、温度报警、数据上报',
    permissions: ['iot:view', 'iot:manage', 'iot:bind', 'alert:view', 'alert:manage', 'container:view'],
    permissionCount: 6, userCount: 3, status: 'active',
    createdAt: '2024-06-01T00:00:00Z', updatedAt: '2025-01-14T20:00:00Z',
  },
];

// ===== G05 审计日志（1,247 条） =====

const auditActions = ['登录', '登出', '创建订单', '修改订单', '删除', '导出报表', '修改配置', '审核', '分配任务', '绑定设备', '角色变更'];
const auditResources = ['订单管理', '集装箱管理', '财务收款', 'IoT设备', '系统配置', 'RBAC权限', '任务管理', '买家管理', 'Dashboard', '报关清关'];
const auditUsers = [
  { name: '张运营', role: '运营经理' },
  { name: '李组长', role: '产地组长' },
  { name: '王船务', role: '船务专员' },
  { name: '赵质检', role: '质检员' },
  { name: '钱财务', role: '财务专员' },
  { name: '孙迪拜', role: '迪拜团队' },
  { name: '周IoT', role: 'IoT管理员' },
  { name: '吴超管', role: '超级管理员' },
];
const auditResults: Array<'success' | 'failure'> = ['success', 'success', 'success', 'success', 'success', 'failure'];

function generateAuditLogs(count: number): AuditLog[] {
  const logs: AuditLog[] = [];
  const baseTime = new Date('2025-01-15T12:00:00Z').getTime();
  const resourcePrefixes = ['ORD', 'CONT', 'FIN', 'CFG', 'CLM', 'IOT'];
  for (let i = 0; i < count; i++) {
    const user = auditUsers[i % auditUsers.length];
    const action = auditActions[i % auditActions.length];
    const prefix = resourcePrefixes[i % resourcePrefixes.length];
    const offsetMinutes = i * 7 + (i % 60) * 3;
    const createdAt = new Date(baseTime - offsetMinutes * 60000).toISOString();
    logs.push({
      id: `AUD${String(i + 1).padStart(5, '0')}`,
      orgId: 'ORG_PLATFORM',
      userId: `USR${String((i % 8) + 1).padStart(3, '0')}`,
      username: user.name,
      action,
      module: auditResources[i % auditResources.length],
      resourceType: prefix,
      resourceId: `${prefix}-2025-${String(randomInt(1, 999)).padStart(3, '0')}`,
      ip: `192.168.${(i % 255) + 1}.${(i * 7) % 255}`,
      requestId: `REQ${String(i + 1).padStart(6, '0')}`,
      beforeData: `${user.name} 执行了${action}操作`,
      afterData: '',
      result: auditResults[i % auditResults.length],
      errorMsg: '',
      createdAt,
    });
  }
  return logs;
}

export const mockAuditLogs: AuditLog[] = generateAuditLogs(1247);

// ===== G05 敏感操作（23 条） =====

const sensitiveOpNames = ['节点冲正', '手动核销', '额度调整', '阈值修改', '理赔审批', '权限变更', '数据删除', '配置回滚'];
const sensitiveOperators = [
  { name: '张运营', role: '运营经理' },
  { name: '赵财务', role: '财务专员' },
  { name: '王风控', role: '风控主管' },
  { name: '李IoT', role: 'IoT管理员' },
  { name: '吴超管', role: '超级管理员' },
];
const sensitiveResources = [
  '订单 ORD-2025-001', '收款 FIN-2025-088', '买家 B-2025-0032', '大蒜温控阈值',
  '工单 CLM-2025-0012', '角色权限 R003', '历史数据 2024-Q3', '系统配置 CFG003',
];
const sensitiveDetails = [
  '将 N03-预冷处理 节点从"已完成"回退至"进行中"',
  '手动核销 USD 85,000 水单，原因为"系统匹配失败"',
  'BluCredit 额度从 $50K 调整至 $120K',
  '将报警上限从 5°C 修改为 4°C',
  '审批通过温度超限理赔 $12,300（船公司 60% 定责）',
  '将质检员角色新增"report:export"权限',
  '删除 2024年Q3 历史审计日志 350 条',
  '将系统配置"trade.maxOrderAmount"从 500000 回滚至 300000',
];

export const mockSensitiveOps: SensitiveOperation[] = Array.from({ length: 23 }, (_, i) => {
  const opIdx = i % sensitiveOpNames.length;
  const operator = sensitiveOperators[i % sensitiveOperators.length];
  const baseTime = new Date('2025-01-15T12:00:00Z').getTime();
  const offsetMinutes = i * 43 + 15;
  return {
    id: `SOP${String(i + 1).padStart(3, '0')}`,
    operationName: sensitiveOpNames[opIdx],
    operator: operator.name,
    roleName: operator.role,
    timestamp: new Date(baseTime - offsetMinutes * 60000).toISOString(),
    resource: sensitiveResources[opIdx],
    ip: `10.0.${(i % 10)}.${(i * 13) % 255}`,
    result: (i % 7 === 0 ? 'failure' : 'success') as 'success' | 'failure',
    details: sensitiveDetails[opIdx],
    riskLevel: (i % 5 === 0 ? 'critical' : 'high') as 'high' | 'critical',
  };
});

// ===== G05 权限矩阵 =====

/** 所有角色 ID 列表 */
export const ROLE_IDS = ['R001', 'R002', 'R003', 'R004', 'R005', 'R006', 'R007', 'R008'];

/** 权限矩阵数据（角色 × 权限） */
export const mockPermissionMatrix: PermissionMatrixRow[] = [
  // 订单管理
  { permission: 'order:view', permissionGroup: '订单管理', roles: { R001: true, R002: true, R003: true, R004: true, R005: true, R006: true, R007: true, R008: false } },
  { permission: 'order:create', permissionGroup: '订单管理', roles: { R001: true, R002: true, R003: false, R004: false, R005: false, R006: false, R007: false, R008: false } },
  { permission: 'order:edit', permissionGroup: '订单管理', roles: { R001: true, R002: true, R003: false, R004: false, R005: false, R006: false, R007: false, R008: false } },
  { permission: 'order:delete', permissionGroup: '订单管理', roles: { R001: true, R002: false, R003: false, R004: false, R005: false, R006: false, R007: false, R008: false } },
  // 集装箱管理
  { permission: 'container:view', permissionGroup: '集装箱管理', roles: { R001: true, R002: true, R003: true, R004: true, R005: true, R006: false, R007: true, R008: true } },
  { permission: 'container:edit', permissionGroup: '集装箱管理', roles: { R001: true, R002: true, R003: false, R004: true, R005: false, R006: false, R007: false, R008: false } },
  // 船务管理
  { permission: 'shipping:view', permissionGroup: '船务管理', roles: { R001: true, R002: true, R003: false, R004: true, R005: false, R006: false, R007: true, R008: false } },
  { permission: 'shipping:manage', permissionGroup: '船务管理', roles: { R001: true, R002: false, R003: false, R004: true, R005: false, R006: false, R007: false, R008: false } },
  // 清关管理
  { permission: 'customs:view', permissionGroup: '清关管理', roles: { R001: true, R002: true, R003: false, R004: false, R005: false, R006: false, R007: true, R008: false } },
  { permission: 'customs:manage', permissionGroup: '清关管理', roles: { R001: true, R002: false, R003: false, R004: false, R005: false, R006: false, R007: true, R008: false } },
  // 财务管理
  { permission: 'finance:view', permissionGroup: '财务管理', roles: { R001: true, R002: true, R003: false, R004: false, R005: false, R006: true, R007: false, R008: false } },
  { permission: 'finance:edit', permissionGroup: '财务管理', roles: { R001: true, R002: false, R003: false, R004: false, R005: false, R006: true, R007: false, R008: false } },
  { permission: 'finance:approve', permissionGroup: '财务管理', roles: { R001: true, R002: false, R003: false, R004: false, R005: false, R006: true, R007: false, R008: false } },
  // IoT 管理
  { permission: 'iot:view', permissionGroup: 'IoT 管理', roles: { R001: true, R002: true, R003: false, R004: false, R005: false, R006: false, R007: false, R008: true } },
  { permission: 'iot:manage', permissionGroup: 'IoT 管理', roles: { R001: true, R002: false, R003: false, R004: false, R005: false, R006: false, R007: false, R008: true } },
  { permission: 'iot:bind', permissionGroup: 'IoT 管理', roles: { R001: true, R002: false, R003: false, R004: false, R005: false, R006: false, R007: false, R008: true } },
  // 报警管理
  { permission: 'alert:view', permissionGroup: '报警管理', roles: { R001: true, R002: true, R003: false, R004: false, R005: false, R006: false, R007: false, R008: true } },
  { permission: 'alert:manage', permissionGroup: '报警管理', roles: { R001: true, R002: false, R003: false, R004: false, R005: false, R006: false, R007: false, R008: true } },
  // 报表
  { permission: 'report:view', permissionGroup: '报表管理', roles: { R001: true, R002: true, R003: true, R004: false, R005: true, R006: true, R007: true, R008: false } },
  { permission: 'report:export', permissionGroup: '报表管理', roles: { R001: true, R002: true, R003: false, R004: false, R005: false, R006: true, R007: false, R008: false } },
  // 系统配置
  { permission: 'system:config_view', permissionGroup: '系统配置', roles: { R001: true, R002: false, R003: false, R004: false, R005: false, R006: true, R007: false, R008: false } },
  { permission: 'system:config_edit', permissionGroup: '系统配置', roles: { R001: true, R002: false, R003: false, R004: false, R005: false, R006: false, R007: false, R008: false } },
  // 买家管理
  { permission: 'buyer:view', permissionGroup: '买家管理', roles: { R001: true, R002: true, R003: false, R004: false, R005: false, R006: true, R007: false, R008: false } },
  // 任务管理
  { permission: 'task:view', permissionGroup: '任务管理', roles: { R001: true, R002: true, R003: true, R004: false, R005: false, R006: false, R007: true, R008: false } },
  { permission: 'task:assign', permissionGroup: '任务管理', roles: { R001: true, R002: true, R003: true, R004: false, R005: false, R006: false, R007: false, R008: false } },
  { permission: 'task:execute', permissionGroup: '任务管理', roles: { R001: true, R002: false, R003: true, R004: false, R005: true, R006: false, R007: true, R008: false } },
];

// ===== G04 系统配置与数据字典 Mock 数据 =====

/** 10 节点 SLA 默认配置（nodeCode 对齐数据库 node_code 枚举；写法B canonical 节点） */
export const DEFAULT_SLA_NODES: SlaNode[] = [
  { nodeId: 1,  nodeCode: 'order',          nodeName: '签约',     slaHours: 2,   timeoutAction: '预警通知',         role: '运营主管', controlPoint: '',    isHardIntercept: false },
  { nodeId: 2,  nodeCode: 'produce',        nodeName: '备货',     slaHours: 4,   timeoutAction: '预警通知+升级',    role: '质检员',   controlPoint: 'CP-1', isHardIntercept: true },
  { nodeId: 3,  nodeCode: 'pack',           nodeName: '包装',     slaHours: 4,   timeoutAction: '预警通知',         role: '装箱员',   controlPoint: '',    isHardIntercept: false },
  { nodeId: 4,  nodeCode: 'load',           nodeName: '装柜',     slaHours: 4,   timeoutAction: '预警通知+升级',    role: '报关员',   controlPoint: 'CP-1', isHardIntercept: true },
  { nodeId: 5,  nodeCode: 'export_customs', nodeName: '出口报关', slaHours: 12,  timeoutAction: '预警通知+升级',    role: '报关员',   controlPoint: 'CP-2', isHardIntercept: true },
  { nodeId: 6,  nodeCode: 'ship',           nodeName: '海运',     slaHours: 480, timeoutAction: '预警通知',         role: '物流主管', controlPoint: '',    isHardIntercept: false },
  { nodeId: 7,  nodeCode: 'arrive',         nodeName: '到港',     slaHours: 24,  timeoutAction: '预警通知+升级',    role: '报关员',   controlPoint: 'CP-2', isHardIntercept: false },
  { nodeId: 8,  nodeCode: 'import_customs', nodeName: '进口清关', slaHours: 12,  timeoutAction: '预警通知+升级',    role: '报关员',   controlPoint: 'CP-2', isHardIntercept: true },
  { nodeId: 9,  nodeCode: 'warehouse',      nodeName: '海外仓',   slaHours: 24,  timeoutAction: '预警通知',         role: '物流主管', controlPoint: '',    isHardIntercept: false },
  { nodeId: 10, nodeCode: 'deliver',        nodeName: '配送签收', slaHours: 4,   timeoutAction: '预警通知',         role: '质检员',   controlPoint: '',    isHardIntercept: false },
];

/** 温控阈值默认配置（4 品类） */
export const DEFAULT_TEMP_THRESHOLDS: TempThreshold[] = [
  {
    productId: 'garlic', productName: '大蒜',
    target:  { min: -3, max: 0,  label: '目标温度' },
    warning: { min: -5, max: 2,  label: '预警温度' },
    alarm:   { min: -8, max: 5,  label: '报警温度' },
  },
  {
    productId: 'ginger', productName: '生姜',
    target:  { min: 13, max: 15, label: '目标温度' },
    warning: { min: 10, max: 18, label: '预警温度' },
    alarm:   { min: 5,  max: 25, label: '报警温度' },
  },
  {
    productId: 'apple', productName: '苹果',
    target:  { min: 0,  max: 2,  label: '目标温度' },
    warning: { min: -2, max: 4,  label: '预警温度' },
    alarm:   { min: -5, max: 8,  label: '报警温度' },
  },
  {
    productId: 'onion', productName: '洋葱',
    target:  { min: 0,  max: 2,  label: '目标温度' },
    warning: { min: -2, max: 5,  label: '预警温度' },
    alarm:   { min: -5, max: 10, label: '报警温度' },
  },
];

/** 币种汇率默认配置 */
export const DEFAULT_CURRENCIES: CurrencyConfig[] = [
  { code: 'USD', name: '美元',         rate: 7.24, updatedAt: '2025-01-15T08:00:00Z', syncFrequency: '每日同步(08:00 UTC)' },
  { code: 'CNY', name: '人民币',       rate: 1.00, updatedAt: '2025-01-15T08:00:00Z', syncFrequency: '基准货币' },
  { code: 'AED', name: '阿联酋迪拉姆',  rate: 1.97, updatedAt: '2025-01-15T08:00:00Z', syncFrequency: '每日同步(08:00 UTC)' },
  { code: 'SAR', name: '沙特里亚尔',    rate: 1.93, updatedAt: '2025-01-15T08:00:00Z', syncFrequency: '每日同步(08:00 UTC)' },
  { code: 'SGD', name: '新加坡元',      rate: 5.38, updatedAt: '2025-01-15T08:00:00Z', syncFrequency: '每日同步(08:00 UTC)' },
];

/** 港口航线默认配置 */
export const DEFAULT_ROUTES: RouteConfig[] = [
  { id: 'RT001', origin: '青岛港', destination: '迪拜港(Jebel Ali)', sailingDays: 25, carrier: 'COSCO SHIPPING' },
  { id: 'RT002', origin: '青岛港', destination: '汉堡港',            sailingDays: 35, carrier: 'MAERSK' },
  { id: 'RT003', origin: '宁波港', destination: '鹿特丹港',          sailingDays: 32, carrier: 'CMA CGM' },
  { id: 'RT004', origin: '深圳港', destination: '洛杉矶港',          sailingDays: 18, carrier: 'EVERGREEN' },
];

/** 模板管理默认配置 */
export const DEFAULT_TEMPLATES: TemplateConfig[] = [
  { id: 'TPL001', name: '报关单模板',       status: 'configured', updatedAt: '2025-01-10T00:00:00Z', updatedBy: '报关主管' },
  { id: 'TPL002', name: '装箱单模板',       status: 'configured', updatedAt: '2025-01-08T00:00:00Z', updatedBy: '物流主管' },
  { id: 'TPL003', name: '提单模板',         status: 'pending',    updatedAt: '2024-12-20T00:00:00Z', updatedBy: '物流主管' },
  { id: 'TPL004', name: '植检证模板',       status: 'configured', updatedAt: '2025-01-05T00:00:00Z', updatedBy: '质检员' },
  { id: 'TPL005', name: '商业发票模板',     status: 'configured', updatedAt: '2025-01-12T00:00:00Z', updatedBy: '财务主管' },
  { id: 'TPL006', name: '产地证模板',       status: 'pending',    updatedAt: '2024-12-15T00:00:00Z', updatedBy: '质检员' },
];

// ===== G01 AI Copilot 与公共验真 Mock 数据 =====

/** 默认定责分段 */
const DEFAULT_LIABILITY_SEGMENTS_G01: LiabilitySegment[] = [
  { label: '装柜前', percentage: 40, isCurrent: false, description: '装柜前环节责任', color: '#FB8C00' },
  { label: '目的港（卖方责任）', percentage: 45, isCurrent: true, description: '卖方承担的货损责任', color: '#10B981' },
  { label: '目的港（买家责任）', percentage: 15, isCurrent: false, description: '买家承担的货损责任', color: '#43A047' },
];

/** Mock 对话消息（6条：早报→用户请求→证据报告→追问→定责分析→系统警告） */
export const mockCopilotMessages: AIChatMessage[] = [
  {
    id: 'MSG001',
    role: 'assistant',
    messageType: ChatMessageType.MORNING_REPORT,
    content: '📊 AI 早报 | 2025-07-14\n\n今日运营概览：\n• 活跃订单：326 笔\n• 在途集装箱：48 柜\n• IoT 在线率：97.3%\n• 今日签收：5 柜\n• 新增报警：2 条（温度超限）\n\n⚠ 重点关注：CONT002（大蒜）温度持续偏离目标区间，建议人工复核。',
    timestamp: '2025-07-14T06:00:00Z',
  },
  {
    id: 'MSG002',
    role: 'user',
    messageType: ChatMessageType.USER_REQUEST,
    content: '帮我分析 BLU-2607-0042 柜的 IoT 温度数据，看看有没有异常？',
    timestamp: '2025-07-14T09:15:00Z',
  },
  {
    id: 'MSG003',
    role: 'assistant',
    messageType: ChatMessageType.EVIDENCE_REPORT,
    content: '已为您调取 BLU-2607-0042 柜（大蒜 / 40尺冷藏柜）的 IoT 温度记录，以下是证据链分析结果：\n\n📋 舱位信息：CONT002 / 船名 COSCO SHIPPING ARIES 038W\n📅 航程：上海 → 迪拜（Jebel Ali）\n🌡 温度设定：-3°C ~ 0°C（目标区间）\n\n⚠ 发现问题：\n• 7月12日 14:32 — 温度升至 2.1°C（超目标上限）\n• 7月13日 03:15 — 温度升至 4.8°C（触发报警阈值）\n• 累计超限时长：11.3 小时\n\n基于 IoT 数据链证据，AI 初步定责建议：船公司承担 60% 责任（承运期间温控失职），装柜前环节 40%（预冷不足）。',
    timestamp: '2025-07-14T09:15:03Z',
    metadata: {
      iotDataSummary: 'BLU-2607-0042 | 大蒜 | 40尺冷藏柜 | 目标温度 -3°C~0°C | 实际范围 -2.8°C~4.8°C | 超限 11.3h',
      liabilityProgress: 45,
      liabilitySegments: DEFAULT_LIABILITY_SEGMENTS_G01,
      requiresManualReview: false,
      manualReviewReasons: [],
      downloadableReportUrl: '/api/copilot/evidence/MSG003/download',
      ticketTransferUrl: '/admin/claim-rules?preset=MSG003',
    },
  },
  {
    id: 'MSG004',
    role: 'user',
    messageType: ChatMessageType.USER_REQUEST,
    content: '那定责比例确定了吗？船公司会认吗？需要走什么流程？',
    timestamp: '2025-07-14T09:16:30Z',
  },
  {
    id: 'MSG005',
    role: 'assistant',
    messageType: ChatMessageType.EVIDENCE_REPORT,
    content: '关于定责比例和流程，分析如下：\n\n📊 定责建议（置信度 78%）：\n• 装柜前环节（40%）：预冷温度 -2.8°C 未达 -3°C 目标，存在轻微预冷不足\n• 船公司（60%）：承运期间出现 4.8°C 峰值，超出 SLA 约定的 4°C 报警线\n\n📝 建议流程：\n1. 下载 IoT 证据包（含温度曲线 + 时间戳 + GPS）\n2. 提交至 BluGuard 理赔引擎自动定责\n3. 人工复核确认后生成工单\n4. 通知船公司并提供证据链\n\n💡 提示：BLU-2607-0042 柜已投保 BluShield 冷链险，可启动快速理赔通道。',
    timestamp: '2025-07-14T09:16:35Z',
    metadata: {
      iotDataSummary: 'BLU-2607-0042 | 定责置信度 78% | 推荐流程：证据包→BluGuard→人工复核→通知船公司',
      liabilityProgress: 78,
      liabilitySegments: [
        { label: '装柜前', percentage: 40, isCurrent: false, description: '预冷温度未达 -3°C 目标', color: '#FB8C00' },
        { label: '船公司', percentage: 60, isCurrent: true, description: '承运期间峰值 4.8°C，超限 11.3h', color: '#10B981' },
        { label: '平台', percentage: 100, isCurrent: false, description: '平台综合保障兜底', color: '#43A047' },
      ],
      requiresManualReview: true,
      manualReviewReasons: [
        '定责置信度 78%，低于 85% 自动通过阈值',
        '涉及船公司外部责任认定，需人工确认',
        '货值 USD 185,000 超过自动理赔上限',
      ],
      downloadableReportUrl: '/api/copilot/evidence/MSG005/download',
      ticketTransferUrl: '/admin/claim-rules?preset=MSG005',
    },
  },
  {
    id: 'MSG006',
    role: 'system',
    messageType: ChatMessageType.SYSTEM_WARNING,
    content: '⚠ 人工复核拦截触发！BLU-2607-0042 柜证据链分析已完成，但因以下原因需人工介入：\n\n1. 定责置信度 78%（< 85% 阈值）\n2. 涉及船公司外部责任认定\n3. 货值 USD 185,000 超过自动理赔上限\n\n系统已自动生成工单 CLM-2025-0042，请前往 BluGuard 理赔规则引擎处理。',
    timestamp: '2025-07-14T09:16:40Z',
  },
];

/** Mock Token 链接（5 条） */
export const mockTokenLinks: TokenLink[] = [
  {
    id: 'TOK001',
    url: 'https://bluplanet.com/verify/eh72k3m9x1a4b6c8',
    label: 'BLU-2607-0042 证据包',
    expiryDate: '2025-07-19T09:00:00Z',
    accessCount: 12,
    maxAccess: 0,
    isValid: true,
  },
  {
    id: 'TOK002',
    url: 'https://bluplanet.com/verify/fg93n4p0y2c7d9e1',
    label: 'BLU-2607-0038 温度报告',
    expiryDate: '2025-07-16T18:00:00Z',
    accessCount: 47,
    maxAccess: 100,
    isValid: true,
  },
  {
    id: 'TOK003',
    url: 'https://bluplanet.com/verify/hj04q5r1z3d8e0f2',
    label: 'BLU-2607-0035 定责报告',
    expiryDate: '2025-07-12T12:00:00Z',
    accessCount: 103,
    maxAccess: 200,
    isValid: false,
  },
  {
    id: 'TOK004',
    url: 'https://bluplanet.com/verify/km15t6s2a4e9f1g3',
    label: 'BLU-2607-0048 四证校核',
    expiryDate: '2025-07-21T08:00:00Z',
    accessCount: 3,
    maxAccess: 0,
    isValid: true,
  },
  {
    id: 'TOK005',
    url: 'https://bluplanet.com/verify/ln26u7t3b5f0g2h4',
    label: 'BLU-2607-0051 IoT 日志',
    expiryDate: '2025-07-15T20:00:00Z',
    accessCount: 89,
    maxAccess: 100,
    isValid: true,
  },
];

/** Mock 公共验真数据 */
export const mockVerification: PublicVerification = {
  hash: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
  hashAlgorithm: 'SHA-256',
  qrCodeData: 'BLUPLANET:VERIFY:e3b0c44298fc1c149afbf4c8996fb924',
  verificationCount: 1_247,
  lastVerified: '2025-07-14T08:30:00Z',
  description: '扫描此 QR 码可验证 BluPlanet 平台签发的证据链哈希存证。所有 IoT 数据和定责报告均通过 SHA-256 上链存证，确保不可篡改。',
};

/** Mock AI 能力清单（6 项） */
export const mockAICapabilities: AICapability[] = [
  {
    id: 'CAP001',
    name: '证据链组装',
    description: '自动聚合 IoT 温度/湿度/震动数据 + 时间戳 + GPS，生成结构化证据链',
    status: AICapabilityStatus.ACTIVE,
    icon: 'account_tree',
  },
  {
    id: 'CAP002',
    name: '理赔定责',
    description: '基于证据链 + 合约条款 + 历史判例，AI 自动生成定责建议比例',
    status: AICapabilityStatus.ACTIVE,
    icon: 'gavel',
  },
  {
    id: 'CAP003',
    name: '温度预测',
    description: '基于历史温度曲线 + 航线气象数据，预测未来 24h 温度走势',
    status: AICapabilityStatus.ACTIVE,
    icon: 'device_thermostat',
  },
  {
    id: 'CAP004',
    name: 'ETA 预测',
    description: '结合 AIS + 气象 + 港口拥堵数据，动态预测集装箱到港时间',
    status: AICapabilityStatus.DEVELOPING,
    icon: 'schedule',
  },
  {
    id: 'CAP005',
    name: '信用评级',
    description: '基于交易历史 + 付款记录 + 履约率，AI 动态评估买家信用等级',
    status: AICapabilityStatus.DEVELOPING,
    icon: 'trending_up',
  },
  {
    id: 'CAP006',
    name: 'RFQ 寻源',
    description: '智能匹配采购需求与全球供应商，自动生成报价对比分析',
    status: AICapabilityStatus.PLANNED,
    icon: 'travel_explore',
  },
];

/** Mock HITL 强制人工复核规则（5 条） */
export const mockHITLRules: HITLRule[] = [
  {
    id: 'HITL001',
    category: '定责置信度不足',
    description: '当 AI 定责置信度低于 85% 阈值时，自动拦截并转入人工复核队列',
    trigger: 'liabilityConfidence < 85%',
    priority: HITLPriority.HIGH,
  },
  {
    id: 'HITL002',
    category: '高货值订单',
    description: '单柜货值超过 USD 100,000 时，AI 定责结论必须经人工确认后方可生效',
    trigger: 'cargoValue > 100000',
    priority: HITLPriority.CRITICAL,
  },
  {
    id: 'HITL003',
    category: '外部责任认定',
    description: '涉及船公司、港口、报关行等外部方的责任认定，须经法务/运营人工审核',
    trigger: 'liabilityTarget in [carrier, port, customs]',
    priority: HITLPriority.HIGH,
  },
  {
    id: 'HITL004',
    category: '温度超限超24小时',
    description: '当 IoT 温度超限累计超过 24 小时，自动升级为人工优先处理工单',
    trigger: 'tempExceedDuration > 24h',
    priority: HITLPriority.CRITICAL,
  },
  {
    id: 'HITL005',
    category: '首次理赔买家',
    description: '首次发起理赔的买家，需人工核实历史履约记录后决定是否适用快速通道',
    trigger: 'buyerClaimCount == 0',
    priority: HITLPriority.HIGH,
  },
];

/** Mock AI 统计数据 */
export const mockAIStats: AIStats = {
  totalCalls: 128_450,
  evidenceChainsGenerated: 3_872,
  claimSuggestions: 1_246,
  adoptionRate: 87,
  avgResponseTime: 2,
  uptimeDays: 183,
};
